import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import {
  getFleetHeatmap,
  getFleetSnapshot,
  type FleetSnapshot,
  type FleetUnit,
  type HeatmapResponse,
} from "@/api/fleet";
import { useRealtime } from "@/hooks/useSocket";

import { FleetFiltersBar, type FleetFilters } from "./components/FleetFiltersBar";
import { FleetLayersToggle, type FleetLayers } from "./components/FleetLayersToggle";
import { FleetMap } from "./components/FleetMap";
import { FleetTotalsBar } from "./components/FleetTotalsBar";
import { UnitDetailDrawer } from "./components/UnitDetailDrawer";
import { UnitsSidebar } from "./components/UnitsSidebar";

export function FleetPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<FleetUnit | null>(null);
  const [filters, setFilters] = useState<FleetFilters>({
    search: "",
    estado: "",
    gerencia: "",
  });
  const [layers, setLayers] = useState<FleetLayers>({
    markers: true,
    heatmap: false,
  });

  const { data } = useQuery({
    queryKey: ["fleet-snapshot"],
    queryFn: getFleetSnapshot,
    refetchInterval: 30_000,
  });

  // Heatmap lazy-loads when the layer is on, defaults to last 24 h.
  const heatmapQuery = useQuery({
    queryKey: ["fleet-heatmap", "24h"],
    enabled: layers.heatmap,
    queryFn: () => {
      const end = new Date();
      const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
      return getFleetHeatmap({
        start: start.toISOString(),
        end: end.toISOString(),
        grid: "medium",
      });
    },
    staleTime: 60_000,
  });

  // Realtime position merge keeps the markers in step between snapshot
  // refetches without forcing a network round-trip.
  useRealtime<{
    unitId: number;
    estado: string;
    latitude: number | null;
    longitude: number | null;
    heading: number | null;
    speedKmh: number | null;
  }>("unit.position", (payload) => {
    qc.setQueryData<FleetSnapshot | undefined>(
      ["fleet-snapshot"],
      (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          units: prev.units.map((u) =>
            u.unitId === payload.unitId
              ? {
                  ...u,
                  estado: payload.estado,
                  latitude: payload.latitude,
                  longitude: payload.longitude,
                  heading: payload.heading,
                  speedKmh: payload.speedKmh,
                  lastHeartbeatAt: new Date().toISOString(),
                }
              : u
          ),
        };
      }
    );
  });

  // unit.track adds an extra heartbeat-bump for the affected unit so
  // the side-list "Latido" timestamp stays fresh between snapshots.
  useRealtime<{
    unitId: number;
    estado: string;
    latitude: number;
    longitude: number;
    speedKmh: number | null;
    heading: number | null;
    at: string;
  }>("unit.track", (payload) => {
    qc.setQueryData<FleetSnapshot | undefined>(
      ["fleet-snapshot"],
      (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          units: prev.units.map((u) =>
            u.unitId === payload.unitId
              ? {
                  ...u,
                  estado: payload.estado || u.estado,
                  latitude: payload.latitude,
                  longitude: payload.longitude,
                  heading: payload.heading ?? u.heading,
                  speedKmh: payload.speedKmh ?? u.speedKmh,
                  lastHeartbeatAt: payload.at,
                }
              : u
          ),
        };
      }
    );
  });

  useRealtime("hit.detected", () => {
    // KPI cards across the app + snapshot itself need refresh.
    qc.invalidateQueries({ queryKey: ["fleet-snapshot"] });
    qc.invalidateQueries({ queryKey: ["hits"] });
    qc.invalidateQueries({ queryKey: ["kpis"] });
  });

  useRealtime("unit.alert", () => {
    qc.invalidateQueries({ queryKey: ["fleet-snapshot"] });
  });

  useRealtime("unit.kpi.refreshed", () => {
    qc.invalidateQueries({ queryKey: ["fleet-snapshot"] });
  });

  const units = useMemo(() => data?.units ?? [], [data]);
  const filtered = useMemo(() => applyFilters(units, filters), [units, filters]);
  const estados = useMemo(
    () => Array.from(new Set(units.map((u) => u.estado))).sort(),
    [units]
  );
  const gerencias = useMemo(
    () =>
      Array.from(
        new Set(
          units
            .map((u) => u.gerencia)
            .filter((g): g is string => Boolean(g))
        )
      ).sort(),
    [units]
  );

  // Keep the drawer's data in sync with the live snapshot.
  const selectedLive = useMemo(() => {
    if (!selected) return null;
    return filtered.find((u) => u.unitId === selected.unitId) ?? selected;
  }, [filtered, selected]);

  return (
    <div className="flex flex-col h-full" data-testid="page-fleet">
      <FleetTotalsBar snapshot={data} />
      <FleetFiltersBar
        value={filters}
        estados={estados}
        gerencias={gerencias}
        onChange={setFilters}
      />
      <div className="flex flex-1 min-h-0">
        <UnitsSidebar
          units={filtered}
          selectedUnitId={selectedLive?.unitId ?? null}
          onSelect={setSelected}
        />
        <div className="flex-1 relative">
          <FleetLayersToggle
            layers={layers}
            onChange={setLayers}
            heatmapAvailable={true}
          />
          <FleetMap
            units={filtered}
            heatmap={(heatmapQuery.data as HeatmapResponse | undefined) ?? null}
            layers={layers}
            onSelectUnit={setSelected}
          />
          <UnitDetailDrawer unit={selectedLive} onClose={() => setSelected(null)} />
        </div>
      </div>
    </div>
  );
}

function applyFilters(
  units: FleetUnit[],
  filters: FleetFilters
): FleetUnit[] {
  const search = filters.search.trim().toLowerCase();
  return units.filter((u) => {
    if (filters.estado && u.estado !== filters.estado) return false;
    if (filters.gerencia && u.gerencia !== filters.gerencia) return false;
    if (!search) return true;
    return (
      u.alias.toLowerCase().includes(search) ||
      (u.placa ?? "").toLowerCase().includes(search) ||
      (u.operadorNombre ?? "").toLowerCase().includes(search)
    );
  });
}
